import React from 'react';
import { Container, Typography, Box, Grid, Paper, Chip } from '@mui/material';
import { motion } from 'framer-motion';
import { styled } from '@mui/material/styles';

// ─── Styled Components ────────────────────────────────────────────────────────

const CasePaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  background: 'rgba(255, 255, 255, 0.04)',
  backdropFilter: 'blur(12px)',
  borderRadius: '16px',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #4CAF50 0%, #2196F3 100%)',
  },
}));

const MetricCard = styled(Box)({
  padding: '16px 12px',
  background: 'rgba(76,175,80,0.07)',
  border: '1px solid rgba(76,175,80,0.18)',
  borderRadius: '12px',
  textAlign: 'center',
});

const SectionLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: '10.5px',
      fontWeight: 700,
      letterSpacing: '1.8px',
      textTransform: 'uppercase',
      color: '#4CAF50',
      mb: 1,
    }}
  >
    {children}
  </Typography>
);

const InsightBox = styled(Box)({
  padding: '14px 16px',
  background: 'rgba(33,150,243,0.07)',
  border: '1px solid rgba(33,150,243,0.18)',
  borderLeft: '3px solid #2196F3',
  borderRadius: '8px',
});

const LearningBox = styled(Box)({
  padding: '14px 18px',
  background: 'rgba(76,175,80,0.05)',
  borderLeft: '3px solid #4CAF50',
  borderRadius: '8px',
});

// ─── Case Study Data ───────────────────────────────────────────────────────────

const caseStudies = [
  {
    tag: 'Case Study 01',
    title: 'Fixing the Leaky Funnel — +40% Activation',
    subtitle: 'Consumer Onboarding · PLG · Growth · A/B Testing',
    metrics: [
      { value: '+40%', label: 'New-User Activation' },
      { value: '50K+', label: 'Monthly Active Users' },
      { value: '34%→14%', label: 'KYC Abandonment' },
      { value: '25%+', label: 'Session Engagement Lift' },
    ],
    problem:
      'Nearly 60% of new users who downloaded the Repos app never completed their first order. For a B2B fuel delivery platform where every user represents a business account worth thousands per month, this was a direct revenue leak that compounded with every new install.',
    discovery:
      'I ran a Mixpanel funnel analysis and identified 3 specific drop-off spikes: KYC upload (34% abandonment), payment setup (22%), and first order placement (18%). Then I conducted 12 user interviews across B2B segments — construction sites, manufacturing plants, logistics companies. The key insight: users weren\'t dropping off because the process was too long. They were dropping off because they didn\'t understand why each step was needed. It was a trust and transparency problem, not a UX problem.',
    solution: [
      'Redesigned onboarding into a progressive disclosure flow — users only see what they need at each step',
      'Added a step-progress indicator with estimated completion time',
      'Added contextual tooltips explaining why KYC and payment info are needed (trust signals)',
      'Introduced async save so users can pause and resume without losing progress',
      'Shipped an A/B test against the original flow — validated results in 3 weeks before full rollout',
    ],
    impact: [
      '+40% new-user activation within 6 weeks of launch',
      'KYC abandonment dropped from 34% → 14% (the largest single drop-off point, now resolved)',
      'Payment step abandonment dropped from 22% → 9%',
      'Active user base grew to 50K+ MAU, tracked via Mixpanel + GA',
    ],
    learning:
      'The biggest unlock wasn\'t the UI redesign — it was the contextual copy. Users didn\'t resist the process; they resisted uncertainty. Data tells you where users struggle. User interviews tell you why. You need both.',
    skills: ['Mixpanel Funnel Analysis', 'User Interviews', 'Progressive Disclosure', 'A/B Testing', 'React / TypeScript', 'PRD Writing', 'PLG', 'North Star Metrics'],
  },
  {
    tag: 'Case Study 02',
    title: 'From Spreadsheets to Self-Serve — 600+ Hrs Saved/Year',
    subtitle: 'Internal Analytics Platform · 0→1 Build · Ops Automation',
    metrics: [
      { value: '600+', label: 'Ops Hours Saved / Year' },
      { value: '60%', label: 'Manual Reporting Eliminated' },
      { value: '20–50', label: 'Team Members Adopted' },
      { value: '< 24hrs', label: 'Data Freshness (was 5–7 days)' },
    ],
    problem:
      'The operations team was running the business on spreadsheets. Every Monday, 3–4 ops members spent 4–6 hours manually pulling data from multiple sources, compiling reports, and emailing them to leadership. By the time decisions were made, the data was already 5–7 days old — the company was making strategic calls on stale information.',
    discovery:
      'I ran a 2-week audit of every report the ops team produced, categorising them by frequency and whether leadership actually acted on them. I also interviewed 8 ops team members and 3 leadership stakeholders. The core insight: they didn\'t want more dashboards. They wanted reports delivered to them, in their inbox, on a schedule, with the right numbers already highlighted. Meeting users where they already work — their inbox — was the real unlock.',
    solution: [
      'Scheduled email delivery — stakeholders receive reports automatically, zero manual effort required',
      'Configurable report builder — ops team defines their own parameters without engineering help',
      'KPI summary cards — leadership gets a 5-metric snapshot at the top of every report',
      'Drill-down capability — if a number looks off, users click into the underlying data',
      'Phased delivery: core MVP in 4 weeks, full feature set shipped in week 8',
    ],
    impact: [
      '60% of manual ops reporting work eliminated entirely',
      '600+ hours saved per year across the ops team',
      'Report delivery time cut from 5–7 days to real-time (under 24 hours)',
      'Platform adopted by 20–50 team members within 8 weeks — zero churn',
    ],
    learning:
      'Delivering reports TO people instead of asking them to come TO a dashboard drove adoption 3x faster than expected. The most important product decision wasn\'t a feature — it was the delivery model.',
    skills: ['Stakeholder Interviews', 'PRD & User Stories', '0→1 Product Build', 'SQL & Analytics', 'Agile / Scrum', 'OKR Planning', 'Data Visualisation', 'RICE Prioritisation'],
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

const CaseStudy = () => {
  return (
    <Box id="caseStudy" sx={{ py: 10, bgcolor: '#0A0A0A' }}>
      <Container maxWidth="lg">

        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <Typography variant="h3" sx={{ mb: 2, textAlign: 'center' }}>
            Product <span style={{ color: '#4CAF50' }}>Case Studies</span>
          </Typography>
          <Typography
            variant="body1"
            color="textSecondary"
            sx={{ textAlign: 'center', mb: 6, maxWidth: 560, mx: 'auto', lineHeight: 1.7 }}
          >
            How I identify the real problem, form a hypothesis, ship a solution, and measure
            what actually changed.
          </Typography>
        </motion.div>

        <Grid container spacing={6}>
          {caseStudies.map((cs, csIdx) => (
            <Grid item xs={12} key={csIdx}>
              <motion.div
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: csIdx * 0.1 }}
              >
                <CasePaper>

                  {/* Tag + Title + Subtitle */}
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#4CAF50', letterSpacing: 2, fontWeight: 700,
                      textTransform: 'uppercase', display: 'block', mb: 0.75,
                    }}
                  >
                    {cs.tag}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {cs.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                    {cs.subtitle}
                  </Typography>

                  {/* ── Impact Metric Cards ── */}
                  <Grid container spacing={2} sx={{ mb: 4 }}>
                    {cs.metrics.map((m, i) => (
                      <Grid item xs={6} sm={3} key={i}>
                        <motion.div
                          initial={{ opacity: 0, scale: 0.88 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: i * 0.08 }}
                        >
                          <MetricCard>
                            <Typography
                              variant="h5"
                              sx={{
                                fontWeight: 700,
                                color: '#4CAF50',
                                lineHeight: 1.15,
                                fontSize: { xs: '1rem', sm: '1.35rem' },
                              }}
                            >
                              {m.value}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              sx={{ display: 'block', mt: 0.5, lineHeight: 1.4 }}
                            >
                              {m.label}
                            </Typography>
                          </MetricCard>
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>

                  {/* ── Content Grid ── */}
                  <Grid container spacing={4}>

                    {/* The Problem */}
                    <Grid item xs={12} md={6}>
                      <SectionLabel>The Problem</SectionLabel>
                      <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.75 }}>
                        {cs.problem}
                      </Typography>
                    </Grid>

                    {/* The Discovery */}
                    <Grid item xs={12} md={6}>
                      <SectionLabel>The Discovery — How I Found the Real Why</SectionLabel>
                      <InsightBox>
                        <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.75 }}>
                          {cs.discovery}
                        </Typography>
                      </InsightBox>
                    </Grid>

                    {/* What I Built */}
                    <Grid item xs={12} md={6}>
                      <SectionLabel>What I Built</SectionLabel>
                      {cs.solution.map((s, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.25 }}>
                          <Box
                            sx={{
                              width: 6, height: 6, borderRadius: '50%',
                              bgcolor: '#4CAF50', mt: 0.8, mr: 1.5, flexShrink: 0,
                            }}
                          />
                          <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.65 }}>
                            {s}
                          </Typography>
                        </Box>
                      ))}
                    </Grid>

                    {/* Results */}
                    <Grid item xs={12} md={6}>
                      <SectionLabel>Results</SectionLabel>
                      {cs.impact.map((item, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.25 }}>
                          <Typography
                            component="span"
                            sx={{
                              color: '#4CAF50', mr: 1.25, mt: 0.1,
                              fontSize: 15, lineHeight: 1, flexShrink: 0,
                            }}
                          >
                            ✓
                          </Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.65 }}>
                            {item}
                          </Typography>
                        </Box>
                      ))}
                    </Grid>

                    {/* Key Learning */}
                    <Grid item xs={12}>
                      <LearningBox>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#4CAF50', fontWeight: 700,
                            textTransform: 'uppercase', letterSpacing: 1.5, display: 'block', mb: 0.75,
                          }}
                        >
                          Key Learning
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.7 }}>
                          {cs.learning}
                        </Typography>
                      </LearningBox>
                    </Grid>

                    {/* Skills Applied */}
                    <Grid item xs={12}>
                      <SectionLabel>Skills Applied</SectionLabel>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {cs.skills.map((skill, idx) => (
                          <Chip
                            key={idx}
                            label={skill}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(76,175,80,0.10)',
                              color: '#4CAF50',
                              border: '1px solid rgba(76,175,80,0.20)',
                              fontSize: '12px',
                            }}
                          />
                        ))}
                      </Box>
                    </Grid>

                  </Grid>
                </CasePaper>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default CaseStudy;